import { MSym } from '@storeo/ui';
import type { Device } from './BrowserFrame';

export type CanvasView = 'vitrine' | 'produto';

/** Barra de controle do Estúdio — inspirada em docs/prototype/lpplat/construtor/code.html
 *  (linhas 8–78), reorganizada em 2 linhas pra não estourar a largura:
 *   linha 1 = identidade da loja + ações (subdomínio, histórico, salvar, publicar);
 *   linha 2 = modo de visualização (Vitrine ⇄ Checkout) + device.
 *  Save/Publish e os seletores são funcionais; undo/redo é visual. */
export function StudioControlBar({
  storeName,
  host,
  storeHref,
  canvasView,
  onCanvasView,
  device,
  onDevice,
  published,
  saving,
  savedAt,
  onSave,
  onPublish,
  publishing,
  panelOpen,
  onTogglePanel,
}: {
  storeName: string;
  host: string;
  storeHref: string;
  canvasView: CanvasView;
  onCanvasView: (v: CanvasView) => void;
  device: Device;
  onDevice: (d: Device) => void;
  published: boolean;
  saving: boolean;
  savedAt: Date | null;
  onSave: () => void;
  onPublish: () => void;
  publishing: boolean;
  panelOpen: boolean;
  onTogglePanel: () => void;
}) {
  return (
    <div className="w-full min-w-0 bg-surface-container-low px-space-md py-space-sm flex flex-col gap-space-sm shadow-md rounded-2xl">
      {/* Linha 1 — identidade + ações */}
      <div className="flex flex-wrap items-center justify-between gap-x-space-md gap-y-space-sm">
        <div className="flex items-center gap-space-xs min-w-0">
          <button
            type="button"
            onClick={onTogglePanel}
            title={panelOpen ? 'Recolher personalização' : 'Abrir personalização'}
            aria-label={panelOpen ? 'Recolher personalização' : 'Abrir personalização'}
            aria-pressed={panelOpen}
            className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high transition-all"
          >
            <MSym name={panelOpen ? 'menu_open' : 'menu'} className="text-[20px]" />
          </button>
          <MSym name="brush" className="text-primary text-[20px] shrink-0" />
          <div className="flex items-center gap-1.5 font-label-md text-label-md min-w-0">
            <span className="text-on-surface-variant hidden sm:inline">Editor Visual</span>
            <span className="text-on-surface-variant/40 hidden sm:inline">/</span>
            <span className="text-primary font-bold truncate">{storeName}</span>
            <span className="px-2 py-0.5 rounded-full bg-secondary-container/20 text-secondary font-label-sm text-label-sm uppercase tracking-wider shrink-0 hidden md:inline">
              White-Label 100%
            </span>
          </div>
        </div>

        <div className="flex items-center gap-space-xs flex-wrap">
          <div className="flex items-center gap-space-xs bg-surface-container px-space-sm py-1.5 rounded-lg shadow-sm min-w-0">
            <MSym name="lock" className="text-secondary text-[16px] shrink-0" fill />
            <a
              className="font-mono text-label-md text-on-surface hover:text-primary transition-colors flex items-center gap-1 min-w-0"
              href={storeHref}
              target="_blank"
              rel="noreferrer"
            >
              <span className="truncate">{host}</span>
              <MSym name="open_in_new" className="text-[14px] text-on-surface-variant shrink-0" />
            </a>
          </div>
          <div className="flex items-center bg-surface-container-lowest rounded-lg p-0.5 shrink-0">
            <button
              type="button"
              className="p-1.5 text-on-surface-variant hover:text-on-surface rounded-md hover:bg-surface-container transition-colors"
              title="Desfazer (Ctrl+Z)"
            >
              <MSym name="undo" className="text-[18px]" />
            </button>
            <button
              type="button"
              className="p-1.5 text-on-surface-variant hover:text-on-surface rounded-md hover:bg-surface-container transition-colors"
              title="Refazer (Ctrl+Y)"
            >
              <MSym name="redo" className="text-[18px]" />
            </button>
          </div>
          <span className="hidden lg:flex items-center gap-1.5 px-space-xs py-1 rounded-md text-on-surface-variant font-label-sm text-label-sm shrink-0">
            <MSym name="cloud_done" className="text-secondary text-[16px]" />
            {savedAt ? `salvo ${savedAt.toLocaleTimeString('pt-BR')}` : 'Salvo na Nuvem'}
          </span>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="px-space-md py-2 rounded-lg bg-surface-container-high text-on-surface font-label-md text-label-md font-semibold disabled:opacity-60 shrink-0 whitespace-nowrap"
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
          <button
            type="button"
            onClick={onPublish}
            disabled={publishing}
            className="bg-linear-to-r from-primary-container to-inverse-primary hover:from-primary hover:to-primary-container text-white px-space-md py-2 rounded-lg font-label-lg text-label-lg font-bold flex items-center gap-1.5 shadow-[0_0_20px_rgba(99,102,241,0.4)] hover:shadow-[0_0_25px_rgba(99,102,241,0.6)] transition-all disabled:opacity-60 shrink-0 whitespace-nowrap"
          >
            <MSym name="publish" className="text-[18px]" />
            <span>{published ? 'Despublicar' : 'Publicar no Subdomínio'}</span>
          </button>
        </div>
      </div>

      {/* Linha 2 — modo de visualização + device */}
      <div className="flex flex-wrap items-center justify-between gap-x-space-md gap-y-space-sm border-t border-surface-container-highest/50 pt-space-sm">
        <div className="inline-flex bg-surface-container-lowest p-1 rounded-lg">
          <button
            type="button"
            onClick={() => onCanvasView('vitrine')}
            className={`flex items-center gap-1.5 px-space-sm py-1.5 rounded-md font-label-md text-label-md transition-all whitespace-nowrap ${
              canvasView === 'vitrine'
                ? 'bg-primary-container text-on-primary-container font-bold shadow-sm'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <MSym name="store" className="text-[16px]" />
            <span>Vitrine Principal</span>
          </button>
          <button
            type="button"
            onClick={() => onCanvasView('produto')}
            className={`flex items-center gap-1.5 px-space-sm py-1.5 rounded-md font-label-md text-label-md transition-all whitespace-nowrap ${
              canvasView === 'produto'
                ? 'bg-primary-container text-on-primary-container font-bold shadow-sm'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <MSym name="point_of_sale" className="text-[16px]" />
            <span>Checkout Transparente</span>
            <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
          </button>
        </div>

        <div className="flex items-center bg-surface-container-lowest p-1 rounded-lg gap-0.5 shadow-inner">
          {(['desktop', 'tablet', 'mobile'] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onDevice(d)}
              title={d === 'desktop' ? 'Desktop' : d === 'tablet' ? 'Tablet' : 'Mobile'}
              className={`p-1.5 rounded-md transition-all ${device === d ? 'bg-surface-container text-primary shadow-sm' : 'text-on-surface-variant hover:text-on-surface'}`}
            >
              <MSym
                name={
                  d === 'desktop' ? 'desktop_windows' : d === 'tablet' ? 'tablet_mac' : 'smartphone'
                }
                className="text-[18px]"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
